import React from 'react';

interface Signatory {
  id: number;
  userId: number;
  username: string;
  signatoryType: string;
  signatureWeight: number;
  signatoryAddress: string;
  isActive: boolean;
  addedAt: string;
}

interface SignatoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  signatories: Signatory[];
  loading: boolean;
  requiredSignatures: number;
}

const SignatoriesModal: React.FC<SignatoriesModalProps> = ({
  isOpen,
  onClose,
  signatories,
  loading,
  requiredSignatures
}) => {
  const getSignatoryTypeColor = (type: string) => {
    switch (type) {
      case 'ADMIN': return '#dc3545';
      case 'PROPERTY_OWNER': return '#28a745';
      case 'BUYER': return '#007bff';
      case 'ESCROW_AGENT': return '#ffc107';
      case 'LEGAL_REPRESENTATIVE': return '#6f42c1';
      case 'INVESTOR': return '#fd7e14';
      default: return '#6c757d';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '1200px', width: '95vw' }}>
        <div className="user-details-modal" style={{ maxWidth: 'none', width: '100%' }}>
          <div className="modal-header">
            <h3>Multi-Signature Wallet Signatories</h3>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          
          <div className="modal-body">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div className="loading-spinner"></div>
                <p>Loading signatories...</p>
              </div>
            ) : signatories.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                <p>No signatories found for this wallet.</p>
              </div>
            ) : (
              <div>
                {/* Signatories Summary */}
                <div style={{
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px',
                }}>
                  <h4 style={{ margin: '0 0 12px 0' }}>Signatories Summary</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                    <div>
                      <strong>Total Signatories:</strong>
                      <div>{signatories.length}</div>
                    </div>
                    <div>
                      <strong>Required Signatures:</strong>
                      <div>{requiredSignatures}</div>
                    </div>
                    <div>
                      <strong>Active Signatories:</strong>
                      <div>{signatories.filter(s => s.isActive).length}</div>
                    </div>
                    <div>
                      <strong>Total Signature Weight:</strong>
                      <div>{signatories.reduce((sum, s) => sum + (s.signatureWeight || 0), 0)}</div>
                    </div>
                  </div>
                </div>

                {/* Signatories List */}
                <div>
                  <h4 style={{ margin: '0 0 16px 0' }}>Signatory Details</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {signatories.map((signatory) => (
                      <div key={signatory.id} style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '16px',
                        backgroundColor: '#ffffff'
                      }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', alignItems: 'start' }}>
                          {/* User Info */}
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                              <div className="user-avatar" style={{
                                backgroundColor: '#e5e7eb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '12px',
                                color: '#6b7280',
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                marginRight: '12px'
                              }}>
                                {signatory.username?.charAt(0) || 'U'}
                              </div>
                              <div>
                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                                  {signatory.username}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                  ID: {signatory.userId}
                                </div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{
                                backgroundColor: getSignatoryTypeColor(signatory.signatoryType),
                                color: '#ffffff',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600',
                                textTransform: 'uppercase'
                              }}>
                                {signatory.signatoryType}
                              </span>
                              <span style={{
                                backgroundColor: signatory.isActive ? '#28a745' : '#dc3545',
                                color: '#ffffff',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '600'
                              }}>
                                {signatory.isActive ? 'ACTIVE' : 'INACTIVE'}
                              </span>
                            </div>
                          </div>

                          {/* Details */}
                          <div>
                            <div style={{ marginBottom: '8px' }}>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                                Signature Weight
                              </div>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                                {signatory.signatureWeight}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                                Added Date
                              </div>
                              <div style={{ fontSize: '14px', color: '#1f2937' }}>
                                {signatory.addedAt ? 
                                  new Date(signatory.addedAt).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  }) : 'N/A'
                                }
                              </div>
                            </div>
                          </div>

                          {/* Address and Actions */}
                          <div>
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                                Wallet Address
                              </div>
                              <div style={{ 
                                fontFamily: 'monospace',
                                fontSize: '11px',
                                color: '#6b7280',
                                backgroundColor: '#f9fafb',
                                padding: '6px 8px',
                                borderRadius: '4px',
                                wordBreak: 'break-all'
                              }}>
                                {signatory.signatoryAddress ? 
                                  `${signatory.signatoryAddress.substring(0, 8)}...${signatory.signatoryAddress.substring(signatory.signatoryAddress.length - 6)}` : 
                                  'N/A'
                                }
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => {
                                  // TODO: Implement edit signatory functionality
                                  alert('Edit signatory functionality coming soon!');
                                }}
                                className="admin-button"
                                style={{ 
                                  fontSize: '11px', 
                                  padding: '6px 10px'
                                }}
                                title="Edit Signatory"
                              >
                                ✏️ Edit
                              </button>
                              {signatories.length > 2 && (
                                <button
                                  onClick={() => {
                                    // TODO: Implement remove signatory functionality
                                    alert('Remove signatory functionality coming soon!');
                                  }}
                                  className="admin-button danger"
                                  style={{ 
                                    fontSize: '11px', 
                                    padding: '6px 10px'
                                  }}
                                  title="Remove Signatory"
                                >
                                  🗑️ Remove
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Add New Signatory Section */}
                <div style={{ 
                  borderTop: '1px solid #e5e7eb', 
                  marginTop: '20px',
                  paddingTop: '16px'
                }}>
                  <h4 style={{ margin: '0 0 12px 0' }}>Add New Signatory</h4>
                  <button 
                    className="admin-button success"
                    onClick={() => {
                      // TODO: Implement add signatory functionality
                      alert('Add signatory functionality coming soon!');
                    }}
                    style={{ fontSize: '12px' }}
                  >
                    ➕ Add New Signatory
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <div className="action-section">
              <button onClick={onClose} className="action-btn secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignatoriesModal; 